import { SourceDataVerificationPermission } from '@/api/dtos';
import ConfigurationForm from '@/components/data/configuration/ConfigurationForm';
import Breadcrumbs from '@/components/layouts/Breadcrumbs';
import PageTitle, { TITLE_SEPARATOR } from '@/components/layouts/PageTitle';
import TrialContext from '@/contexts/data/TrialContext';
import useMetadata from '@/hooks/data/useMetadata';
import useNewConfiguration from '@/hooks/data/useNewConfiguration';
import useTrialConfigurationByTrialCode from '@/hooks/data/useTrialConfigurationByTrialCode';
import usePermissions from '@/hooks/utility/usePermissions';
import { faDatabase, faGlobeOceania } from '@fortawesome/duotone-light-svg-icons';
import { Grid, Paper, styled, Typography, useTheme } from '@mui/material';
import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';



const NewConfiguration: FC = () => {
    const { trialCode, copyId } = useParams();
    
    const { data: trial } = useContext(TrialContext);
    const { data: metadata, loading: metadataLoading, validating: metadataUpdating } = useMetadata(trialCode!, { revalidateOnFocus: false });
    const { data: configuration, loading: configurationLoading, validating: configurationUpdating } = useNewConfiguration(metadata ? trialCode! : undefined, copyId ? +copyId : undefined, { revalidateOnFocus: false });
    const { data: trialConfiguration, loading: trialConfigurationLoading, validating: trialConfigurationUpdating } = useTrialConfigurationByTrialCode(!!metadata ? trialCode : undefined, { revalidateOnFocus: false });
    const theme = useTheme();
    const [ready, setReady] = useState(false);

    useEffect(() => {
        const timeoutId = setTimeout(() => setReady(true), 250);

        return () => {
            clearTimeout(timeoutId);
        }
    }, [])

    const breadcrumbs = useMemo(() => {
        return [
            {
                label: 'Configurations',
                link: `/${trialCode}/configurations`,
                icon: faDatabase
            },
            {
                label: 'New Configuration',
                icon: faDatabase
            }
        ];
    }, [trialCode])

    const loadingData = !ready || metadataLoading || configurationLoading || trialConfigurationLoading;
    const updating = !loadingData && (metadataUpdating || configurationUpdating || trialConfigurationUpdating);

    const { 
        data: [
            hasEditConfiguration
        ] = [], 
        loading: permissionsLoading, 
        validating: permissionsValidating
    } = usePermissions(
        [
            SourceDataVerificationPermission.EditConfiguration
        ], 
        loadingData,
        trial?.id,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        {
            revalidateOnFocus: false
        }
    );

    const loading = loadingData || permissionsLoading || permissionsValidating;

    if (!hasEditConfiguration && !permissionsLoading && !permissionsValidating) {
        return (
            <Navigate to='/error/403' />
        )
    }

    return (
        <>
            <PageTitle title={`New Configuration${TITLE_SEPARATOR}${trial?.name}`} />
            <Breadcrumbs 
                breadcrumbs={breadcrumbs}
            />
            <Typography color={theme.palette.secondary.main} variant="h1" gutterBottom>
                New Configuration
            </Typography>
            <ConfigurationForm
                configuration={ready ? configuration : undefined}
                trialConfiguration={ready ? trialConfiguration : undefined}
                trial={trial}
                metadata={ready ? metadata : undefined}
                loading={loading}
                updating={updating}
            />
        </>
    );
}

export default NewConfiguration;